import { googleMapsAuthFailed, waitForGoogleMaps } from './maps';

/** Great-circle distance in miles, rounded to 2 decimals. */
export function haversineMiles(lat1, lng1, lat2, lng2) {
  const toRad = (deg) => (Number(deg) * Math.PI) / 180;
  const R = 6371e3;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lng2 - lng1);
  const a =
    Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const meters = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return +(meters * 0.000621371).toFixed(2);
}

/** "New York, NY, USA" → "new-york-ny-usa" */
export function slugify(text) {
  if (!text) return '';
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/** "new-york-ny-usa" → "new york ny usa" (good enough for geocoding). */
export function deslugify(slug) {
  if (!slug) return '';
  return decodeURIComponent(String(slug)).replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
}

/** "los angeles" → "Los Angeles" */
export function titleCase(text) {
  return String(text || '').replace(/\b\w/g, (c) => c.toUpperCase());
}

function countryCode(components = []) {
  const country = components.find((c) => c.types?.includes('country'));
  return country ? country.short_name.toLowerCase() : '';
}

const LOOKUP_TIMEOUT_MS = 10_000;

/**
 * Runs a Geocoder request. When Google rejects the API key (referrer, billing,
 * quota) its requests never settle, so give up after a timeout instead of
 * leaving the page loading forever.
 */
async function lookup(request) {
  const ready = await waitForGoogleMaps();
  if (!ready || googleMapsAuthFailed()) throw new Error('Google Maps is unavailable');
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('Location lookup timed out')), LOOKUP_TIMEOUT_MS);
  });
  try {
    const { results } = await Promise.race([new window.google.maps.Geocoder().geocode(request), timeout]);
    return results || [];
  } finally {
    clearTimeout(timer);
  }
}

/** Thrown when the lookup worked but nothing matched (as opposed to the service being down). */
export const NOT_FOUND = 'Location not found';

/** Google rejects with ZERO_RESULTS instead of returning an empty list. */
export const isLocationNotFound = (error) =>
  error?.message === NOT_FOUND || error?.code === 'ZERO_RESULTS' || /ZERO_RESULTS/.test(error?.message || '');

/**
 * Geocodes free text or a place id.
 * Returns { lat, lng, cc, name, formatted, isAirport }.
 */
export async function geocode({ address, placeId }) {
  const results = await lookup(placeId ? { placeId } : { address });
  const result = results[0];
  if (!result) throw new Error(NOT_FOUND);

  return {
    lat: result.geometry.location.lat(),
    lng: result.geometry.location.lng(),
    cc: countryCode(result.address_components),
    name: result.address_components?.[0]?.long_name || address || '',
    formatted: result.formatted_address,
    isAirport: Boolean(result.types?.includes('airport')),
  };
}

/** Coordinates → { cc, name }, preferring the locality's formatted address. */
export async function reverseGeocode(lat, lng) {
  const results = await lookup({ location: { lat, lng } });
  if (!results.length) throw new Error(NOT_FOUND);

  const locality = results.find((r) => r.types?.includes('locality'));
  const sublocality = results.find((r) => r.types?.includes('sublocality'));
  return {
    cc: countryCode(results[0].address_components),
    name: (locality || sublocality || results[0]).formatted_address,
  };
}

/** Splits Google address components into the fields the host property form needs. */
export function parseAddressComponents(components = []) {
  const out = { city: '', state: '', country: '', country_code: '', postal_code: '' };
  for (const c of components) {
    const types = c.types || [];
    if (types.includes('locality')) out.city = c.long_name;
    else if (types.includes('postal_town') && !out.city) out.city = c.long_name;
    else if (types.includes('sublocality_level_1') && !out.city) out.city = c.long_name;
    else if (types.includes('administrative_area_level_1')) out.state = c.long_name;
    else if (types.includes('country')) {
      out.country = c.long_name;
      out.country_code = c.short_name;
    } else if (types.includes('postal_code')) out.postal_code = c.long_name;
  }
  return out;
}

/**
 * Geocodes a request ({ placeId } | { address } | { location }) into the
 * fields the property form needs: { address, city, state, country,
 * country_code, postal_code, lat, lng, formatted }.
 */
export async function geocodeAddress(request) {
  const results = await lookup(request);
  const result = results[0];
  if (!result) throw new Error(NOT_FOUND);

  const parts = parseAddressComponents(result.address_components);
  const get = (type) => result.address_components.find((c) => c.types?.includes(type))?.long_name || '';
  const street = [get('street_number'), get('route')].filter(Boolean).join(' ') || get('premise');

  return {
    ...parts,
    address: street || result.formatted_address,
    formatted: result.formatted_address,
    lat: result.geometry.location.lat(),
    lng: result.geometry.location.lng(),
  };
}
